import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import CustomEventTypes from 'hooks/analytics/customEventTypes'
import { useSegmentInteraction } from 'hooks/analytics/segment'
import { noop } from 'lodash-es'
import { useBeforeunload } from 'react-beforeunload'
import { useParams } from 'react-router'
import { SESSION_INACTIVE_RESCUE_TIME } from 'utility/defaults'
import { useCreateDietitianSessionMutation } from 'types'
import { ISession, removeStoredSession, storedSessions, storeSession } from './sessionsStorage'

interface ITimeTracker {
  isActive?: boolean
  children: ReactNode | ReactNode[]
}

const isValidSession = ({ startedAt, endedAt, userId }: ISession) => {
  return startedAt && endedAt && startedAt < endedAt && (!userId || !isNaN(parseInt(userId)))
}

const defaultValues = {
  endTrackingSession: noop
}

const TimeTrackerContext = createContext(defaultValues)
export const useTimeTracker = () => useContext(TimeTrackerContext)

export const TimeTrackerProvider = ({ isActive: viewIsActive = false, children }: ITimeTracker) => {
  const { track } = useSegmentInteraction()
  const [activeSession, setActiveSession] = useState(false)
  const [userId, setUserId] = useState<string | undefined>(undefined)

  const { id: userIdFromParams } = useParams()

  const [createDietitianSession] = useCreateDietitianSessionMutation({
    onCompleted: (data) => {
      const session = data?.createDietitianSession
      if (!session) return
      track(CustomEventTypes.NutritionistSession, {
        userId: session.userId,
        durationInSeconds: session.durationInSeconds
      })
    }
  })

  const sessionStartTime = useRef<Date | null>(null)

  const sendSessionEvent = useCallback(
    ({ startedAt, endedAt, userId }: ISession) => {
      if (!isValidSession({ startedAt, endedAt, userId })) return

      createDietitianSession({
        variables: {
          userId: userId,
          startedAt: startedAt instanceof Date ? startedAt.toISOString() : startedAt ?? '',
          endedAt: endedAt instanceof Date ? endedAt.toISOString() : endedAt
        }
      })
        .then(() => removeStoredSession({ startedAt, endedAt, userId }))
        .catch(() => storeSession({ startedAt, endedAt, userId }))
    },
    [createDietitianSession]
  )

  const startSession = useCallback(() => {
    setActiveSession(true)
    sessionStartTime.current = new Date()
  }, [])

  const endSession = useCallback(
    (withDelay: boolean = true) => {
      if (!activeSession) {
        return
      }

      setActiveSession(false)
      const sessionEndTime = new Date(Date.now() - (withDelay ? SESSION_INACTIVE_RESCUE_TIME : 0))
      sendSessionEvent({ startedAt: sessionStartTime.current, endedAt: sessionEndTime, userId })
    },
    [sendSessionEvent, activeSession, sessionStartTime, userId]
  )

  useEffect(() => {
    if (activeSession && !viewIsActive) endSession()
    if (!activeSession && viewIsActive) startSession()
  }, [activeSession, endSession, startSession, viewIsActive])

  useEffect(() => {
    endSession(false)
    setUserId(userIdFromParams)
    // Each session is removed from storage only once its mutation succeeds, so
    // an interrupted flush keeps unsent sessions instead of losing them.
    storedSessions().forEach((session: ISession) => sendSessionEvent(session))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdFromParams, sendSessionEvent])

  useBeforeunload(() => {
    if (!activeSession || !sessionStartTime.current) return
    storeSession({ startedAt: sessionStartTime.current, endedAt: new Date(), userId })
  })

  return (
    <TimeTrackerContext.Provider value={{ endTrackingSession: endSession }}>
      {children}
    </TimeTrackerContext.Provider>
  )
}
